/**
 * src/plugins/finance/payroll/payroll-service.ts
 *
 * Universal Payroll Service & Double-Entry Ledger Posting Bridge
 * Coordinates staff roster, calculates payslips, and emits balanced PostingInstructions.
 */

import { generateUUIDv7 } from '../../../crypto/uuidv7.js';
import type { PostingInstruction, PostingInstructionLine } from '../financial-contracts.js';
import type { DoubleEntryLedgerEngine } from '../ledger/double-entry-ledger.js';
import type {
  PayrollMemberProfile,
  PayrollRun,
  CalculatedPayslip,
} from './payroll-contracts.js';
import { PayrollCalculator } from './payroll-calculator.js';

export class PayrollService {
  private readonly employees = new Map<string, PayrollMemberProfile>();
  private readonly payrollRuns = new Map<string, PayrollRun>();
  private readonly calculator = new PayrollCalculator();

  constructor(private readonly ledger?: DoubleEntryLedgerEngine) {}

  /**
   * Enrolls a new employee or contractor (Mesh user or external non-mesh staff).
   */
  public enrollEmployee(
    profileInput: Omit<PayrollMemberProfile, 'employeeId' | 'createdAt' | 'updatedAt'>
  ): PayrollMemberProfile {
    const employeeId = `emp_${generateUUIDv7()}`;
    const now = Date.now();
    const profile: PayrollMemberProfile = {
      ...profileInput,
      employeeId,
      createdAt: now,
      updatedAt: now,
    };
    this.employees.set(employeeId, profile);
    return profile;
  }

  public updateEmployee(
    employeeId: string,
    updates: Partial<Omit<PayrollMemberProfile, 'employeeId' | 'createdAt' | 'updatedAt'>>
  ): PayrollMemberProfile {
    const existing = this.employees.get(employeeId);
    if (!existing) {
      throw new Error(`Employee profile '${employeeId}' not found`);
    }
    const updated: PayrollMemberProfile = {
      ...existing,
      ...updates,
      updatedAt: Date.now(),
    };
    this.employees.set(employeeId, updated);
    return updated;
  }

  public getEmployee(employeeId: string): PayrollMemberProfile | undefined {
    return this.employees.get(employeeId);
  }

  public getAllEmployees(tenantId?: string): readonly PayrollMemberProfile[] {
    const all = Array.from(this.employees.values());
    return tenantId ? all.filter((e) => e.tenantId === tenantId) : all;
  }

  /**
   * Executes a payroll run for all active employees in a given tenant and period.
   */
  public async executePayrollRun(params: {
    tenantId: string;
    legalEntityId: string;
    periodKey: string; // e.g. "2026-08"
    jurisdictionCode: string;
    approvedBy: string;
  }): Promise<{ payrollRun: PayrollRun; postingInstruction: PostingInstruction }> {
    const payrollRunId = `prun_${generateUUIDv7()}`;
    const employees = this.getAllEmployees(params.tenantId).filter(
      (e) => e.status === 'active' && e.legalEntityId === params.legalEntityId
    );

    const payslips: CalculatedPayslip[] = employees.map((emp) =>
      this.calculator.calculatePayslip(emp, payrollRunId, params.periodKey)
    );

    const totalGrossMinor = payslips.reduce((sum, p) => sum + p.grossSalaryMinor, 0);
    const totalEmployerStatutoryMinor = payslips.reduce((sum, p) => sum + p.employerStatutoryTotalMinor, 0);
    const totalEmployeeStatutoryMinor = payslips.reduce((sum, p) => sum + p.employeeStatutoryTotalMinor, 0);
    const totalNetMinor = payslips.reduce((sum, p) => sum + p.netSalaryMinor, 0);

    const functionalCurrency = employees[0]?.currency || 'USD';

    // ── Generate Balanced Double-Entry PostingInstruction ─────────────────────
    // Dr 6000 Gross Salaries Expense = totalGrossMinor
    // Dr 6010 Employer Statutory Expense = totalEmployerStatutoryMinor
    // Cr 2100 Net Salaries Payable = totalNetMinor
    // Cr 2200 Statutory Withholdings Payable = totalEmployeeStatutoryMinor + totalEmployerStatutoryMinor
    const totalStatutoryWithholdingMinor = totalEmployeeStatutoryMinor + totalEmployerStatutoryMinor;

    const lines: PostingInstructionLine[] = [
      {
        purpose: 'payroll_expense',
        accountId: '6000', // Salaries Expense
        debitMinor: totalGrossMinor,
        creditMinor: 0,
        transactionCurrencyCode: functionalCurrency,
        transactionAmountMinor: totalGrossMinor,
        description: `Salaries & Wages Expense — Period ${params.periodKey}`,
      },
    ];

    if (totalEmployerStatutoryMinor > 0) {
      lines.push({
        purpose: 'payroll_expense',
        accountId: '6010', // Employer Statutory Contributions
        debitMinor: totalEmployerStatutoryMinor,
        creditMinor: 0,
        transactionCurrencyCode: functionalCurrency,
        transactionAmountMinor: totalEmployerStatutoryMinor,
        description: `Employer Statutory Pension & Health Contributions — Period ${params.periodKey}`,
      });
    }

    lines.push({
      purpose: 'payroll_liability',
      accountId: '2100', // Net Salaries Payable (Clearing)
      debitMinor: 0,
      creditMinor: totalNetMinor,
      transactionCurrencyCode: functionalCurrency,
      transactionAmountMinor: totalNetMinor,
      description: `Net Salaries Payable to Staff — Period ${params.periodKey}`,
    });

    if (totalStatutoryWithholdingMinor > 0) {
      lines.push({
        purpose: 'statutory_payable',
        accountId: '2200', // Statutory Tax & Pension Withholdings Payable
        debitMinor: 0,
        creditMinor: totalStatutoryWithholdingMinor,
        transactionCurrencyCode: functionalCurrency,
        transactionAmountMinor: totalStatutoryWithholdingMinor,
        description: `Statutory Tax & Pension Remittance Payable (LHDN/URA/EPF/NSSF) — Period ${params.periodKey}`,
      });
    }

    const postingInstruction: PostingInstruction = {
      instructionId: `inst_prun_${payrollRunId}`,
      documentId: payrollRunId,
      tenantId: params.tenantId,
      legalEntityId: params.legalEntityId,
      branchId: 'MAIN',
      accountingBook: 'general',
      functionalCurrency,
      lines,
      producedByJurisdictionModule: `payroll_${params.jurisdictionCode.toLowerCase()}`,
      producedAtRulesetVersion: '2026.1.0',
      producedAt: new Date().toISOString(),
    };

    let journalEntryId: string | undefined;
    if (this.ledger) {
      const entry = await this.ledger.postEntry(postingInstruction, params.periodKey);
      journalEntryId = entry.entryId;
    }

    const payrollRun: PayrollRun = {
      payrollRunId,
      tenantId: params.tenantId,
      legalEntityId: params.legalEntityId,
      periodKey: params.periodKey,
      jurisdictionCode: params.jurisdictionCode,
      totalGrossMinor,
      totalEmployerStatutoryMinor,
      totalEmployeeStatutoryMinor,
      totalNetMinor,
      payslips,
      status: journalEntryId ? 'posted_to_ledger' : 'approved',
      journalEntryId,
      executedAt: Date.now(),
      approvedBy: params.approvedBy,
    };

    this.payrollRuns.set(payrollRunId, payrollRun);
    return { payrollRun, postingInstruction };
  }

  public getPayrollRun(payrollRunId: string): PayrollRun | undefined {
    return this.payrollRuns.get(payrollRunId);
  }

  public getAllPayrollRuns(tenantId?: string): readonly PayrollRun[] {
    const all = Array.from(this.payrollRuns.values());
    return tenantId ? all.filter((r) => r.tenantId === tenantId) : all;
  }
}
